import { Injectable, inject, signal } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactSupportPageComponent } from '../../../features/prob/components/contact-support/contact-support-page/contact-support-page.component';
import { UserService } from './user.service';
import { catchError, take, timeout } from 'rxjs/operators';
import { of } from 'rxjs';
import { OktaWrapperService } from '../../../../services/framework';

/**
 * Opens the Contact Support form, from wherever it is asked for.
 *
 * There are two entry points to one form - the ? at the top of every page, and the
 * Contact Support item in the left-hand menu - and they must behave identically. This
 * holds the one copy of "how the form is opened", including pre-filling it from the
 * signed-in user, so the two cannot drift apart.
 *
 * A MODAL RATHER THAN A ROUTE, deliberately. The form is thirty seconds of typing and
 * the reader is usually mid-task when they reach for it; navigating away to a page of
 * their own would cost them their place - the same reasoning that gives Help its own
 * tab rather than routing in place. The menu item carries the route of the Contact
 * Support help section so that a ctrl-click still leads somewhere real, but a plain
 * click never navigates.
 */
@Injectable({
  providedIn: 'root'
})
export class ContactSupportService {

  private readonly modalService = inject(NgbModal);
  private readonly userService = inject(UserService);
  private readonly oktaWrapper = inject(OktaWrapperService);

  /** True while the form is being prepared (user lookup in flight) and not yet shown. */
  public readonly isOpening = signal(false);

  // ----------------- Public methods ----------------------------------------

  /**
   * Opens the form, pre-filled with who the sender is.
   *
   * The user is read at the moment of opening rather than held, so an impersonated
   * session raises support as the identity it is currently working under - which is
   * the identity whose problem is being reported.
   */
  public open(): void {
    this.isOpening.set(true);

    this.userService.getCurrentUser().pipe(
      take(1),
      timeout(3000),
      catchError(() => of(null))
    ).subscribe(user => {
      const isFallback = user === null;
      const oktaUser = this.oktaWrapper.getUser();

      const modalRef = this.modalService.open(ContactSupportPageComponent, {
        size: 'lg',
        centered: true
      });

      modalRef.componentInstance.isFallback = isFallback;
      modalRef.componentInstance.model = {
        firstName: isFallback ? (oktaUser?.firstName ?? '') : user.firstName,
        lastName: isFallback ? (oktaUser?.lastName ?? '') : user.lastName,
        company: isFallback ? '' : user.companyName,
        email: isFallback ? (oktaUser?.email ?? '') : '',
        comments: ''
      };

      this.isOpening.set(false);

      // Dismissal is not a failure - it is someone changing their mind - so both
      // outcomes are swallowed rather than surfaced.
      modalRef.result.then(() => { }, () => { });
    });
  }
}
